import React, { useState, useEffect } from 'react';

// --- Dummy Data ---
const dummyQuestions = [
  {
    id: 1,
    text: "Which planet in our solar system is known as the Red Planet?",
    options: ["Venus", "Mars", "Jupiter", "Saturn"],
    correctAnswer: 1
  },
  {
    id: 2,
    text: "Who painted the Mona Lisa?",
    options: ["Vincent van Gogh", "Pablo Picasso", "Leonardo da Vinci", "Michelangelo"],
    correctAnswer: 2
  },
  {
    id: 3,
    text: "What is the chemical symbol for Gold?",
    options: ["Ag", "Au", "Fe", "Cu"],
    correctAnswer: 1
  },
  {
    id: 4,
    text: "Which of the following is the longest river in the world?",
    options: ["Amazon River", "Nile River", "Yangtze River", "Mississippi River"],
    correctAnswer: 1
  },
  {
    id: 5,
    text: "In which year did the Titanic sink?",
    options: ["1912", "1905", "1918", "1923"],
    correctAnswer: 0
  },
  {
    id: 6,
    text: "What is the capital city of Australia?",
    options: ["Sydney", "Melbourne", "Canberra", "Perth"],
    correctAnswer: 2
  },
  {
    id: 7,
    text: "Which element makes up the majority of the Earth's atmosphere?",
    options: ["Oxygen", "Carbon Dioxide", "Nitrogen", "Hydrogen"],
    correctAnswer: 2
  },
  {
    id: 8,
    text: "Who wrote 'Romeo and Juliet'?",
    options: ["Charles Dickens", "William Shakespeare", "Mark Twain", "Jane Austen"],
    correctAnswer: 1
  },
  {
    id: 9,
    text: "What is the hardest natural substance on Earth?",
    options: ["Gold", "Iron", "Diamond", "Platinum"],
    correctAnswer: 2
  },
  {
    id: 10,
    text: "How many continents are there on Earth?",
    options: ["5", "6", "7", "8"],
    correctAnswer: 2
  }
];

export interface ExamQuestion {
  id: string | number;
  text: string;
  options: string[];
  correctAnswer: number;
}

export interface ExamModuleProps {
  title?: string;
  description?: string;
  questions?: ExamQuestion[];
  durationInSeconds?: number;
  onComplete?: (score: number, total: number, answers: Record<number, number>) => void;
  onExit?: () => void;
}

type ExamState = 'intro' | 'exam' | 'results';
type AnswerMap = Record<number, number>; // question index -> selected option index

export default function ExamModule({
  title = "General Knowledge Quiz",
  description = "Test your knowledge. Good luck!",
  questions = dummyQuestions,
  durationInSeconds = 600,
  onComplete,
  onExit
}: ExamModuleProps) {
  const [examState, setExamState] = useState<ExamState>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [timeLeft, setTimeLeft] = useState(durationInSeconds);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showPalette, setShowPalette] = useState(false);

  // Timer logic
  useEffect(() => {
    let timer: any;
    if (examState === 'exam' && timeLeft > 0) {
      timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    } else if (timeLeft === 0 && examState === 'exam') {
      handleSubmit();
    }
    return () => clearInterval(timer);
  }, [examState, timeLeft]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleSelectOption = (optIndex: number) => {
    if (isReviewMode) return;
    setAnswers((prev) => ({ ...prev, [currentQuestionIndex]: optIndex }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    setExamState('results');
    setIsReviewMode(false);

    if (onComplete) {
      // Calculate final score since state hasn't updated the derived one yet
      const finalScore = Object.keys(answers).reduce((acc, key) => {
        const qIndex = parseInt(key);
        if (answers[qIndex] === questions[qIndex].correctAnswer) return acc + 1;
        return acc;
      }, 0);
      onComplete(finalScore, questions.length, answers);
    }
  };

  const startExam = () => {
    setExamState('exam');
    setAnswers({});
    setCurrentQuestionIndex(0);
    setTimeLeft(durationInSeconds);
    setIsReviewMode(false);
  };

  const reviewExam = () => {
    setExamState('exam');
    setIsReviewMode(true);
    setCurrentQuestionIndex(0);
  };

  // Derived state for results
  const score = Object.keys(answers).reduce((acc, key) => {
    const qIndex = parseInt(key);
    if (answers[qIndex] === questions[qIndex].correctAnswer) return acc + 1;
    return acc;
  }, 0);
  
  const answeredCount = Object.keys(answers).length;
  const unansweredCount = questions.length - answeredCount;

  // --- Render Functions ---

  if (examState === 'intro') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 font-sans">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 p-10 text-center transform transition-all hover:scale-105 duration-500">
          <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-800 mb-4">{title}</h1>
          <p className="text-slate-500 mb-8 leading-relaxed">{description}</p>
          <button 
            onClick={startExam}
            className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:shadow-indigo-300 transition-all active:scale-95"
          >
            Start Exam
          </button>
        </div>
      </div>
    );
  }

  if (examState === 'results') {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 font-sans">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 p-10 text-center">
          <h2 className="text-3xl font-extrabold text-slate-800 mb-2">Exam Completed!</h2>
          <p className="text-slate-500 mb-8">Here is your performance summary</p>
          
          <div className="relative w-48 h-48 mx-auto mb-8">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" stroke="#f1f5f9" strokeWidth="10" fill="none" />
              <circle cx="50" cy="50" r="40" stroke={percentage >= 50 ? "#10b981" : "#ef4444"} strokeWidth="10" fill="none" strokeDasharray={`${percentage * 2.51} 251`} className="transition-all duration-1000 ease-out" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-black text-slate-800">{percentage}%</span>
              <span className="text-sm font-medium text-slate-400">Score</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-10">
            <div className="bg-emerald-50 rounded-2xl p-4">
              <div className="text-2xl font-black text-emerald-600">{score}</div>
              <div className="text-xs font-bold text-emerald-800 uppercase tracking-wide mt-1">Correct</div>
            </div>
            <div className="bg-rose-50 rounded-2xl p-4">
              <div className="text-2xl font-black text-rose-600">{answeredCount - score}</div>
              <div className="text-xs font-bold text-rose-800 uppercase tracking-wide mt-1">Incorrect</div>
            </div>
            <div className="bg-slate-100 rounded-2xl p-4">
              <div className="text-2xl font-black text-slate-600">{unansweredCount}</div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-1">Skipped</div>
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <button onClick={reviewExam} className="flex-1 py-4 bg-slate-100 text-slate-700 rounded-2xl font-bold hover:bg-slate-200 transition-colors active:scale-95">
              Review
            </button>
            <button onClick={startExam} className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all active:scale-95">
              Retake
            </button>
          </div>
          {onExit && (
            <button onClick={onExit} className="w-full py-4 border-2 border-slate-200 text-slate-600 rounded-2xl font-bold hover:bg-slate-50 transition-colors active:scale-95">
              Exit Exam
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- EXAM MODE & REVIEW MODE ---
  const currentQ = questions[currentQuestionIndex];
  const hasAnsweredCurrent = answers[currentQuestionIndex] !== undefined;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-white px-6 py-4 border-b border-slate-200 flex justify-between items-center sticky top-0 z-20 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <div>
            <h2 className="font-bold text-slate-800">{isReviewMode ? 'Review Mode' : title}</h2>
            <p className="text-xs font-medium text-slate-500">Question {currentQuestionIndex + 1} of {questions.length}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setShowPalette(!showPalette)}
            className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors hidden sm:block"
            title="Question Palette"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
          </button>
          
          {!isReviewMode && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm ${timeLeft < 60 ? 'bg-rose-100 text-rose-600 animate-pulse' : 'bg-slate-100 text-slate-700'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {formatTime(timeLeft)}
            </div>
          )}
          {isReviewMode && (
            <button onClick={() => setExamState('results')} className="px-4 py-2 bg-slate-800 text-white rounded-xl font-bold text-sm hover:bg-slate-900 transition-colors">
              Exit Review
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto p-4 sm:p-8 flex flex-col relative pb-32">
        
        {/* Question Palette Overlay */}
        {showPalette && (
          <div className="absolute top-0 right-4 sm:right-8 bg-white shadow-2xl rounded-2xl border border-slate-100 p-4 z-30 w-72 mt-2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-slate-800">Question Palette</h3>
              <button onClick={() => setShowPalette(false)} className="text-slate-400 hover:text-slate-600"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {questions.map((q, i) => {
                const isAnswered = answers[i] !== undefined;
                const isCurrent = currentQuestionIndex === i;
                let bgClass = "bg-slate-100 text-slate-600 hover:bg-slate-200";
                
                if (isReviewMode) {
                  const isCorrect = answers[i] === q.correctAnswer;
                  if (isAnswered) {
                    bgClass = isCorrect ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
                  } else {
                    bgClass = "bg-slate-100 text-slate-400";
                  }
                } else if (isAnswered) {
                  bgClass = "bg-indigo-100 text-indigo-700";
                }
                
                if (isCurrent) {
                  bgClass += " ring-2 ring-indigo-500 ring-offset-2";
                }

                return (
                  <button 
                    key={i} 
                    onClick={() => { setCurrentQuestionIndex(i); setShowPalette(false); }}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm transition-all ${bgClass}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Question Area */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 sm:p-10 mb-8 flex-1 transition-all duration-300 ease-in-out" key={currentQuestionIndex}>
          <div className="flex items-start justify-between mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 leading-tight">
              {currentQ.text}
            </h3>
            {isReviewMode && (
               <div className="ml-4 shrink-0 mt-1">
                 {answers[currentQuestionIndex] === currentQ.correctAnswer ? (
                   <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm shadow-sm shadow-emerald-100">
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Correct
                   </span>
                 ) : (
                   <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 font-bold text-sm shadow-sm shadow-rose-100">
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> {answers[currentQuestionIndex] === undefined ? 'Skipped' : 'Incorrect'}
                   </span>
                 )}
               </div>
            )}
          </div>
          
          <div className="space-y-4">
            {currentQ.options.map((opt, idx) => {
              const isSelected = answers[currentQuestionIndex] === idx;
              const isCorrectOpt = currentQ.correctAnswer === idx;
              
              let cardClass = "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 text-slate-700";
              let badgeClass = "bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600";
              let showIcon = null;

              if (isReviewMode) {
                if (isCorrectOpt) {
                  cardClass = "border-emerald-500 bg-emerald-50 text-emerald-900 shadow-sm shadow-emerald-100";
                  badgeClass = "bg-emerald-500 text-white";
                  showIcon = <svg className="w-6 h-6 text-emerald-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>;
                } else if (isSelected && !isCorrectOpt) {
                  cardClass = "border-rose-300 bg-rose-50 text-rose-900";
                  badgeClass = "bg-rose-500 text-white";
                  showIcon = <svg className="w-6 h-6 text-rose-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>;
                } else {
                  cardClass = "border-slate-100 bg-slate-50 text-slate-400 opacity-60";
                  badgeClass = "bg-slate-200 text-slate-400";
                }
              } else if (isSelected) {
                cardClass = "border-indigo-600 bg-indigo-50 text-indigo-900 shadow-md shadow-indigo-100 ring-1 ring-indigo-600";
                badgeClass = "bg-indigo-600 text-white";
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleSelectOption(idx)}
                  disabled={isReviewMode}
                  className={`group w-full flex items-center p-4 sm:p-5 rounded-2xl border-2 transition-all duration-200 text-left ${cardClass} ${!isReviewMode && !isSelected ? 'active:scale-[0.99]' : ''}`}
                >
                  <span className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-xl font-bold text-lg mr-4 transition-colors ${badgeClass}`}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span className="text-lg font-semibold">{opt}</span>
                  {showIcon}
                </button>
              );
            })}
          </div>
        </div>

      </main>

      {/* Fixed Footer Navigation */}
      <footer className="bg-white border-t border-slate-200 px-6 py-4 sm:py-6 fixed bottom-0 w-full z-20">
        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-6 flex items-center gap-4">
            <span className="text-xs font-bold text-slate-400 w-10 text-right">{Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%</span>
            <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-500 rounded-full transition-all duration-500 ease-out" 
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
            <button 
              onClick={() => setShowPalette(!showPalette)}
              className="sm:hidden text-indigo-600 font-bold text-sm bg-indigo-50 px-3 py-1.5 rounded-lg"
            >
              Palette
            </button>
          </div>

          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handlePrev}
              disabled={currentQuestionIndex === 0}
              className={`flex-1 sm:flex-none sm:w-40 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold transition-all ${currentQuestionIndex === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg> Previous
            </button>
            
            <button
              onClick={handleNext}
              className={`flex-1 sm:flex-none sm:w-48 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold transition-all shadow-lg active:scale-95 ${!isReviewMode && currentQuestionIndex === questions.length - 1 ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-emerald-200' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200'}`}
            >
              {!isReviewMode && currentQuestionIndex === questions.length - 1 ? 'Submit Exam' : 'Next Question'}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
